<template>
  <teleport to="body">
    <transition :name="contentTransition">
      <div
        v-if="isActive"
        ref="overlay"
        class="overlay critical-hide"
        :class="overlayClasses"
      >
        <div
          ref="contentContainer"
          class="overlay__container"
          :class="containerClasses"
        >
          <slot :is-active="isActive" name="body" />
        </div>
      </div>
    </transition>

    <transition name="fade">
      <button
        v-if="isActive"
        class="overlay__background"
        type="button"
        tabindex="-1"
        aria-hidden="true"
        @click.prevent="closeOverlay"
      />
    </transition>
  </teleport>
</template>

<script>
  /**
   * Component: Overlay
   * ------------------------------------------------------------------------------
   * - Demonstrative example component.
   * @param {String} [direction] - Direction in which the overlay animates in. Used for drawers
   * A direction of 'left' would mean the drawer opens from the right and animates left.
   * @param {Boolean} isActive - The overlay is visible.
   * @param {String} [type] - Type of overlay. Aligns with CSS classes:
   * 'popup', 'drawer' 'drawer-bottom' etc.
   * @param {String} exampleHeadingText - Heading Text to display.
   * @param {String} exampleHeadingType - Theme Settings for h1-h6 values.
   * @param {String} exampleCopy - Paragraph text to display.
   */
  import { nextTick } from 'vue'

  import cssClasses from '~/helpers/classes'

  export default {
    name: 'Overlay',

    props: {
      direction: {
        type: String,
        default: 'right',
      },
      isActive: {
        type: Boolean,
        default: false,
      },
      parentClass: {
        type: String,
        default: '',
      },
      type: {
        type: String,
        default: 'popup',
      },
    },

    emits: ['close-overlay'],

    data() {
      return {
        scrollElement: false,
      }
    },

    computed: {
      /**
       * Calculate a Vue Transition name for the entry modal.
       * - Used for 'drawers'.
       * - These align with the transitions in vue-transitions.scss.
       * @returns {String}
       */
      contentTransition() {
        if (this.type === 'popup') {
          return 'fade'
        }

        const directions = ['left', 'right', 'up']

        if (!directions.includes(this.direction)) {
          return 'fade'
        }

        return `slide-${this.direction}`
      },

      /**
       * Calculate classes for the containers.
       * @returns {String}
       */
      containerClasses() {
        /**
         * '.drawer' is taken through legacy classes.
         */
        const type = this.type === 'drawer' ? 'popup-drawer' : this.type

        let classes = `${type}__container`

        if (this.parentClass) {
          classes += ` ${this.parentClass}__container`
        }

        return classes
      },

      /**
       * Calculates CSS classes to apply based on the settings provided.
       * `type="drawer" and `direction="up" will generate:
       *  `drawer drawer--left`
       * @returns {String}
       */
      overlayClasses() {
        if (this.type === 'popup') {
          return `popup ${this.parentClass}`
        }

        /**
         * '.drawer' is taken through legacy classes.
         */
        const type = this.type === 'drawer' ? 'popup-drawer' : this.type

        if (!this.direction) {
          return `${type} ${this.parentClass}`
        }

        return `${type} ${type}--${this.direction} ${this.parentClass}`
      },
    },

    watch: {
      /**
       * Watch the active state of the overlay.
       * - Typically, Overlays will by dynamically imported, so
       */
      isActive() {
        if (!this.isActive) {
          document.body.classList.remove(cssClasses.locked)

          return
        }

        nextTick(() => {
          this.setScrollElement()
          this.setCustomProperties()
          document.body.classList.add(cssClasses.locked)
        })
      },
    },

    beforeUnmount() {
      document.body.classList.remove(cssClasses.locked)
    },

    methods: {
      /**
       * Set custom properties.
       * - Sets the height of a header element, if it exists.
       * - Allows for iOS scrolling of content area.
       */
      setCustomProperties() {
        if (this.type !== 'drawer') {
          return
        }

        const headerElement = this.$refs.contentContainer.querySelector(
          '[js-overlay="headerElement"]'
        )

        if (!headerElement) {
          return
        }

        this.$refs.overlay.style.setProperty(
          '--header-height',
          `${headerElement.offsetHeight}px`
        )
      },

      /**
       * Set the element in the overlay which will be scrollable.
       * - Required param to pass to body-scroll-lock
       * - CSS styles are applied to `overlay__container`
       * - <template #body> should include this class as well
       *   as the required data attribute.
       */
      setScrollElement() {
        if (!this.isActive) {
          return
        }

        this.scrollElement = this.$refs.contentContainer.querySelector(
          '[js-overlay="scrollElement"]'
        )
      },

      /**
       * Close the overlay.
       * - Emits an event to the parent
       * - Logic to toggle the 'overlay' component should be handled
       *   within the parent component.
       * - Toggling 'isActive' here allows for the overlay transition to
       *   run before the component unmounts.
       */
      closeOverlay() {
        this.$emit('close-overlay')
      },
    },
  }
</script>
